import { __awaiter } from '../../_virtual/_tslib.js';
import EventEmitter from 'eventemitter3';
import { getWalletBookWallet } from '@dynamic-labs/wallet-book';
import { WalletBookSingleton } from './WalletBookSingleton.js';

/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-empty-interface */
const Chains = [
    'ETH',
    'FLOW',
    'SOL',
    'EVM',
    'ALGO',
    'STARK',
    'ATOM',
    'COSMOS',
];
const socialProviders = [
    'google',
    'facebook',
    'apple',
    'github',
    'bitbucket',
    'gitlab',
    'linkedin',
    'twitter',
    'discord',
    'twitch',
    'microsoft',
];
class WalletConnectorBase extends EventEmitter {
    /**
     * We store the constructor props so that we can use them later on
     * in getMobileOrInstalledWallet which may fall back to a different class
     * but will need the original constructor props.
     * @param props - constructor props
     */
    constructor(props) {
        super();
        this.chainRpcProviders = undefined;
        this.didSetup = false;
        /**
         * @default false
         */
        this.canConnectViaEmail = false;
        /**
         * IF the wallet needs to be connected via a custodial service
         * such as Blocto, this will be true.
         * @default false
         */
        this.canConnectViaCustodialService = false;
        /**
         * If the wallet is not installed, and can be connected via a QR code,
         * this will be true.
         * @default false
         */
        this.canConnectViaQrCode = false;
        /**
         * Whether this connector can be connected via social login.
         * @default false
         */
        this.canConnectViaSocial = false;
        /**
         * Flag if it is wallet Connect
         *
         * @default false
         */
        this.isWalletConnect = false;
        /**
         * Additional resources to add to the message to be signed
         *
         * @default undefined
         */
        this.providerResources = undefined;
        /**
         * Requires switching network in the wallet itself
         * @default undefined
         */
        this.switchNetworkOnlyFromWallet = undefined;
        /**
         * Whether the connector has been initialized
         * @default true
         */
        this.isInitialized = true;
        this.constructorProps = props;
        this._walletBookInstance = WalletBookSingleton.getOrCreate(props.walletBook);
        if (this.walletBook === undefined) {
            throw new Error('WalletConnectorBase was not called with super(props) and is missing wallet-book');
        }
    }
    /**
     * Add the event listeners for the wallet and connect
     * with event emitter.
     *
     * This will be removed when all wallets are migrated to
     * use the event emitter "emit" method
     *
     * Ticket todo - QNTM-88
     */
    initEventListener() {
        if (this.didSetup)
            return;
        this.didSetup = true;
        this.setupEventListeners({
            onAccountChange: (accounts) => __awaiter(this, void 0, void 0, function* () {
                this.emit('accountChange', { accounts });
            }),
            onChainChange: (chain) => __awaiter(this, void 0, void 0, function* () {
                this.emit('chainChange', { chain });
            }),
            onDisconnect: () => __awaiter(this, void 0, void 0, function* () {
                this.emit('disconnect');
            }),
        });
    }
    get walletBook() {
        return this._walletBookInstance.walletBook;
    }
    filterByWalletBook() {
        try {
            getWalletBookWallet(this.walletBook, this.name);
            return true;
        }
        catch (_a) {
            return false;
        }
    }
    connect() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.fetchPublicAddress();
        });
    }
    /**
     * Generic function to close the wallet connection
     * Originally implemented for WalletConnect, but it is used
     * for anything that needs to be "logged out" or cleaned up
     *
     * @default Promise<undefined>
     */
    endSession() {
        return Promise.resolve();
    }
    /**
     * Gets the public address of the wallet
     *
     * @default Promise<undefined>
     */
    fetchPublicAddress(opts) {
        return Promise.resolve(undefined);
    }
    /**
     * Gets the balance of the wallet
     *
     * @default Promise<undefined>
     */
    getBalance() {
        return Promise.resolve(undefined);
    }
    /**
     * Get the address silently
     *
     * @default Promise<[]>
     */
    getConnectedAccounts() {
        return Promise.resolve([]);
    }
    /**
     * Gets the deep link of the wallet
     *
     * @default undefined
     */
    getDeepLink() {
        return undefined;
    }
    getNetwork() {
        return Promise.resolve(undefined);
    }
    /**
     * Gets current network of connected wallet
     *
     * @default Promise<undefined>
     */
    getNameService() {
        return Promise.resolve(undefined);
    }
    getRpcProvider() {
        return Promise.resolve(undefined);
    }
    getSession() {
        return __awaiter(this, void 0, void 0, function* () {
            return Promise.resolve();
        });
    }
    getSigner() {
        return Promise.resolve(undefined);
    }
    getWeb3Provider() {
        return undefined;
    }
    /**
     * Initialize the wallet connector with any async operations
     *
     * @default Promise<void>
     */
    init() {
        return Promise.resolve();
    }
    /**
     * Check if the wallet is installed on the browser
     *
     * @default false
     */
    isInstalledOnBrowser() {
        return false;
    }
    /**
     * Normalized wallet name
     */
    get key() {
        return this.name.replace(/\W/g, '').toLowerCase();
    }
    /**
     * Whether the wallet connector should fall back to a different wallet connector
     * This is called after the object is instantiated, so it can't be a static property
     * and will return the appropriate instance of the wallet connector
     * @returns WalletConnector
     * @default this
     */
    getMobileOrInstalledWallet() {
        return this;
    }
    /**
     * In most cases this is an alias for `signMessage`
     *
     * @default Promise<undefined>
     */
    proveOwnership(messageToSign) {
        return this.signMessage(messageToSign);
    }
    /**
     * Set up event listeners for the wallet
     *
     * @default void
     */
    setupEventListeners(listeners) {
        return;
    }
    /**
     * Sign a message
     *
     * @default Promise<undefined>
     */
    signMessage(messageToSign) {
        return Promise.resolve(undefined);
    }
    /**
     * Whether the wallet supports network switching
     *
     * @default false
     */
    supportsNetworkSwitching() {
        return false;
    }
    switchNetwork({ networkName, networkChainId, }) {
        return Promise.resolve(undefined);
    }
    /**
     * Tear down event listeners for the wallet
     * @default void
     */
    teardownEventListeners() {
        return;
    }
    /**
     * Sign a message
     *
     * @default Promise<undefined>
     */
    getSupportedNetworks() {
        return Promise.resolve(undefined);
    }
}

export { Chains, WalletConnectorBase, socialProviders };
